import { watchEffect, type Directive, type DirectiveBinding, type WatchStopHandle } from 'vue';
import { usePermissionStore } from '@/stores/permissions';
import { useUserStore } from '@/stores/user';
import type { PermissionCategory } from '@/types/permissions';

/**
 * Permission Directives - Clean & Reactive
 *
 * Usage:
 * v-can-write="'CALCULATOR'"
 * v-can-read="'ANALYSIS'"
 * v-can-delete="'TARGETS'"
 *
 * Features:
 * - Reactive updates when permissions change
 * - Type-safe module names
 * - Clean template syntax
 * - Automatic show/hide behavior
 */

interface PermissionElement extends HTMLElement {
  _permissionCleanup?: () => void;
}

// The display value the element had before we touched it. Captured once per
// element: reading it back from el.style would return our own 'none' and the
// element could then never be revealed again.
const originalDisplays = new WeakMap<HTMLElement, string>();

const getOriginalDisplay = (el: PermissionElement): string => {
  if (!originalDisplays.has(el)) {
    originalDisplays.set(el, el.style.display || '');
  }
  return originalDisplays.get(el) || '';
};

// Helper function to create permission directive
const createPermissionDirective = (
  action: 'read' | 'write' | 'delete'
): Directive<PermissionElement, PermissionCategory> => {
  // Binds the element's visibility to the store. Re-runs whenever the user's
  // roles change, so permissions that arrive after mount (first login, role
  // update, company switch) reveal the element instead of stranding it hidden.
  const bind = (el: PermissionElement, moduleKey: PermissionCategory): WatchStopHandle => {
    const permissionStore = usePermissionStore();
    const userStore = useUserStore();
    const originalDisplay = getOriginalDisplay(el);

    return watchEffect(() => {
      // Roles not loaded yet — defer the decision instead of hiding. Hiding here
      // is what used to lock write actions away for the whole session.
      if (userStore.user === null) return;

      let hasPermission = false;
      try {
        switch (action) {
          case 'read':
            hasPermission = permissionStore.canRead(moduleKey);
            break;
          case 'write':
            hasPermission = permissionStore.canWrite(moduleKey);
            break;
          case 'delete':
            hasPermission = permissionStore.canDelete(moduleKey);
            break;
        }
      } catch (error) {
        console.error(`[v-can-${action}] Error checking permission:`, error);
        hasPermission = false;
      }

      el.style.display = hasPermission ? originalDisplay : 'none';
    });
  };

  const teardown = (el: PermissionElement) => {
    if (el._permissionCleanup) {
      el._permissionCleanup();
      delete el._permissionCleanup;
    }
  };

  return {
    mounted(el: PermissionElement, binding: DirectiveBinding<PermissionCategory>) {
      const moduleKey = binding.value;
      if (!moduleKey) {
        console.warn(`[v-can-${action}] Module key is required`);
        return;
      }

      const stop = bind(el, moduleKey);

      el._permissionCleanup = () => {
        stop();
        el.style.display = getOriginalDisplay(el);
      };
    },

    updated(el: PermissionElement, binding: DirectiveBinding<PermissionCategory>) {
      // Only the module key can change here; the effect handles permission
      // changes on its own.
      if (binding.value === binding.oldValue) return;

      teardown(el);

      const moduleKey = binding.value;
      if (!moduleKey) {
        console.warn(`[v-can-${action}] Module key is required`);
        return;
      }

      const stop = bind(el, moduleKey);

      el._permissionCleanup = () => {
        stop();
        el.style.display = getOriginalDisplay(el);
      };
    },

    unmounted(el: PermissionElement) {
      teardown(el);
    }
  };
};

// Export individual directives
export const vCanRead = createPermissionDirective('read');
export const vCanWrite = createPermissionDirective('write');
export const vCanDelete = createPermissionDirective('delete');

// Export as plugin for easier registration
export default {
  install(app: any) {
    app.directive('can-read', vCanRead);
    app.directive('can-write', vCanWrite);
    app.directive('can-delete', vCanDelete);
  }
};
